import { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { EditableProvider } from 'hb-react/frontoffice/apps/Forum/contexts/EditableContext';
import {
  ForumProvider,
  useForumContext,
} from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { makeConfigUrls } from 'hb-react/frontoffice/apps/Forum/utils/configUrls';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { EvErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';
import { type AppPageConfig } from 'hb-react/types';

import { useCanCreateDiscussion } from './hooks/useCanCreateDiscussion';
import ForumCreate from './pages/ForumCreate';
import ForumDetailsPage from './pages/ForumDetails';
import ForumEdit from './pages/ForumEdit';
import ForumIndex from './pages/ForumIndex';

interface ForumAppProps {
  appPageConfig: AppPageConfig;
  topicName?: string;
}

const ForumApp = ({ appPageConfig, topicName }: ForumAppProps) => {
  const { topicId } = useTopicContext();
  const configUrls = makeConfigUrls(topicId);

  return (
    <EvErrorBoundary
      context={{
        tags: {
          module: `FO|Forum`,
        },
      }}
    >
      <ForumProvider
        appPageConfig={appPageConfig}
        configUrls={configUrls}
        topicName={topicName}
      >
        <ForumRouter />
      </ForumProvider>
    </EvErrorBoundary>
  );
};

export const ForumRouter = () => (
  <Routes>
    <Route path="t/:id/*">
      <Route index element={<ForumDetailsPage />} />
      <Route
        path="edit"
        element={
          <ControlledRoute>
            <ForumEdit />
          </ControlledRoute>
        }
      />
    </Route>
    <Route
      path="t/new"
      element={
        <ControlledRoute>
          <ForumCreate />
        </ControlledRoute>
      }
    />
    <Route index element={<ForumIndex />} />
  </Routes>
);

const ControlledRoute = ({ children }) => {
  const { configUrls } = useForumContext();
  const canCreateDiscussion = useCanCreateDiscussion();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canCreateDiscussion) {
      navigate('..');
    }
  }, [canCreateDiscussion, navigate, configUrls]);

  if (!canCreateDiscussion) {
    return null;
  }

  return <EditableProvider>{children}</EditableProvider>;
};

export default ForumApp;
